import { Component, Input, OnInit } from '@angular/core';
import { AnnualReportProvider } from '@modeso/aumico-lib-annualreport-fe';
import { IAnnualReport, IPrintOptions } from '@modeso/types__aumico-lib-annualreport-be';
import Debug from 'debug';
import { filter } from 'rxjs/operators';

const debug = Debug('aumico:project-reports-fe:AnnualReportCoverPage');

@Component({
    selector: 'aumico-annual-report-cover-page',
    template: `
        <aumico-annual-report-cover-display
            [stylePreferenceOptions]="printOptions.styleOptions"
            [companyName]="companyName"
            [companyUID]="isShowUID ? companyUID : ''"
            [companyStreet]="companyStreet"
            [companyAdditionToAddress]="companyAdditionToAddress"
            [companyCity]="companyCity"
            [companyZip]="companyZip"
            [signaturePlaceDate]="signaturePlaceDate"
            [reportTitlePart1]="mainTitleFieldValue"
            [reportTitlePart2]="subtitleFieldValue"
            [reportTitlePart3]="dateFieldValue"
            [showAdjustmentInSums]="showAdjustmentInSums"
            [draftLabel]="draftLabel"
            [reportLocale]="reportLocale"
        >
        </aumico-annual-report-cover-display>
    `,
})
export class AnnualReportCoverPage implements OnInit {
    companyName: string;
    companyUID: string;
    companyStreet: string;
    companyCity: string;
    companyAdditionToAddress: string | undefined;
    companyZip: string;
    signaturePlaceDate: string | null = null;
    isShowUID = false;
    mainTitle: string;
    subtitle: string;
    dateTitle: string;

    private _printOptions: IPrintOptions;
    public get printOptions(): IPrintOptions {
        return this._printOptions;
    }
    @Input()
    public set printOptions(value: IPrintOptions) {
        this._printOptions = value;
        this.getSignatureData(value);
        this.updateTitles(value);
    }

    @Input() companyLogo: string | ArrayBuffer;
    @Input() reportLocale: string;

    mainTitleFieldValue: string;
    subtitleFieldValue: string;
    dateFieldValue: string;
    showAdjustmentInSums: boolean;
    draftLabel: string | null = null;

    reportTitleDateFrom: string;
    reportTitleDateTo: string;

    currentAnnualReport: IAnnualReport;

    constructor(private annualReportProvider: AnnualReportProvider) {
        this.handleGetActiveAnnualReport = this.handleGetActiveAnnualReport.bind(this);
    }

    ngOnInit() {
        this.annualReportProvider
            .getActiveAnnualReport$()
            .pipe(filter((activeAnnualReport) => activeAnnualReport != null))
            .subscribe(this.handleGetActiveAnnualReport);
    }

    private handleGetActiveAnnualReport(report: IAnnualReport): void {
        this.currentAnnualReport = report;
        this.extractData(report);
    }

    private extractData(report: IAnnualReport): void {
        this.draftLabel = report.isSealed ? null : 'aumico_cover_page_draft_label';

        if (report.generalInformation != null && report.generalInformation.company != null) {
            this.companyName = report.generalInformation.company.name;
            this.companyCity = report.generalInformation.company.city;
            this.companyZip = report.generalInformation.company.zip;
            this.companyUID = report.generalInformation.company.UID;
            this.companyStreet = report.generalInformation.company.street;
            this.companyAdditionToAddress = report.generalInformation.company.additionToAddress;
            this.reportTitleDateFrom = report.generalInformation.balancePreviousYearDate;
            this.reportTitleDateTo = report.generalInformation.balanceSheetDate;
        }
    }

    private updateTitles(value: IPrintOptions): void {
        if (value && value.displayOptions) {
            this.mainTitleFieldValue = value.displayOptions.mainTitle;
            this.subtitleFieldValue = value.displayOptions.subtitle;
            this.dateFieldValue = value.displayOptions.dateTitle;
            this.showAdjustmentInSums = value.displayOptions.showAdjustmentInSums;
        } else {
            this.subtitleFieldValue = null;
            this.dateFieldValue = null;
            this.showAdjustmentInSums = false; // Ensure this is set to false when displayOptions is not available
        }
    }

    private getSignatureData(printOptions: IPrintOptions): void {
        this.signaturePlaceDate = [printOptions.signatureOptions.place, printOptions.signatureOptions.date]
            .filter((value) => value != null && value.trim().length !== 0)
            .join(', ');

        this.isShowUID = printOptions.displayOptions.showUIDonCover;
    }
}
